import { Map } from 'immutable';
import projectActions from 'redux/project/actions';
import actions from './actions';

// Returned fresh at every use — the stored application object is handed straight to components,
// so a shared instance risks one of them mutating the blank state everyone else falls back to.
// Every collection the application view maps over has to be present and empty here: the fetch
// error case swaps this in while applicationLoading goes false, so the views render it for real.
const emptyApplication = () => ({
  name: '',
  email: '',
  applications: [],
  attachments: [],
  calendarSlots: [],
  comments: [],
  events: [],
  tags: [],
});

/**
 * Normalises the resolver's error message into the three cases the UI can act on. Access is
 * granted per project only (`AccessSection.Project`), so an application-scoped denial always
 * really means the project is out of reach — the project-level state handles that one.
 */
const classifyApplicationError = (message) => {
  if (!message) return 'generic';
  if (message.indexOf('access.denied') !== -1) return 'denied';
  if (message.indexOf('not.found') !== -1) return 'notFound';
  return 'generic';
};

const initState = new Map({
  application: emptyApplication(),
  applicationError: null,
  applicationGdpred: false,
  applications: [],
  applicationLoading: true,
  applications_loading: true,
  applicationRatingMutating: false,
  showComments: false,
  applicationCommentEditorVisible: false,
  applicationCommentMutating: false,
  applicationCommentEditorContent: '',
  applicationCommentEditorContentRaw: '',
  applicationCommentEditorInvalid: false,
  applicationCommentEditorCommentId: null,
  showGdprStuff: true,
  hasCandidateMissedAnyInterview: false,
  applicationPayPerPerformance: undefined,
  applicationPayPerPerformanceLoading: false,
  loadPayPerPerformance: false,
  applicationUnlockPayPerPerformanceMutating: false,
  cardWidth: 0, // default width for the card, used for tag rendering
});

export default function applicationReducer(state = initState, action) {
  switch (action.type) {
    /* single application reducer */
    case actions.APPLICATION_FETCHING_DONE:
      return state.set('application', action.payload);
    case actions.APPLICATION_ANONYMIZED_STATE_CHANGED: {
      const application = state.get('application');
      return application?.id === action.payload.applicationId
        ? state.set('application', {
            ...application,
            anonymizedApplicationState: action.payload.state,
          })
        : state;
    }
    case actions.APPLICATION_LOADING_DONE:
      return state.set('applicationLoading', false).set('loadPayPerPerformance', true);
    // return state;
    case actions.APPLICATION_FETCH: // has saga side effect
      return state
        .set('applicationLoading', true)
        .set('applicationGdpred', false)
        .set('applicationError', null);
    case actions.APPLICATION_FETCHING_ERROR:
      // Reset the application too: the previously previewed candidate must not stay on screen
      // behind an error state.
      return state
        .set('applicationLoading', false)
        .set('application', emptyApplication())
        .set(
          'applicationError',
          classifyApplicationError(action.payload && action.payload.message)
        );
    case actions.APPLICATION_LOADING_START: // no saga side effect
      return state.set('applicationLoading', true);

    /* single application rating mutation */
    case actions.APPLICATION_RATING_MUTATION_START:
      if (action.payload.id.includes(state.get('application').id)) {
        return state.set('applicationRatingMutating', true).set('application', {
          ...state.get('application'),
          rating: action.payload.rating,
        });
      }
      return state.set('applicationRatingMutating', true);

    case actions.APPLICATION_RATING_MUTATION_ERROR:
      return state.set('applicationRatingMutating', false);
    case actions.APPLICATION_RATING_MUTATION_DONE:
      return state
        .set('applicationRatingMutating', false)
        .set('application', { ...state.get('application'), rating: action.payload.rating });

    /* comments */
    case actions.APPLICATION_COMMENT_ADD_MUTATION_LOADING:
      return state.set('applicationCommentMutating', true);
    case actions.APPLICATION_COMMENT_EDIT_MUTATION_DONE:
      return state
        .set('application', {
          ...state.get('application'),
          comments: action.payload.comments,
        })
        .set('applicationCommentMutating', false);
    case actions.APPLICATION_COMMENT_ADD_MUTATION_DONE:
      return state
        .set('application', {
          ...state.get('application'),
          comments: action.payload.comments,
        })
        .set('applicationCommentMutating', false);
    case actions.APPLICATION_COMMENT_EDITOR_TOGGLE:
      return state.set('applicationCommentEditorVisible', action.payload);
    case actions.APPLICATION_COMMENT_EDITOR_SET:
      return state.set('applicationCommentEditorContent', action.payload);
    case actions.APPLICATION_COMMENT_EDITOR_SET_RAW:
      return state.set('applicationCommentEditorContentRaw', action.payload);
    case actions.APPLICATION_COMMENT_EDITOR_INVALID_TOGGLE:
      return state.set('applicationCommentEditorInvalid', action.payload);
    case actions.APPLICATION_COMMENT_EDITOR_SET_COMMENT_ID:
      return state.set('applicationCommentEditorCommentId', action.payload);

    /* single application selectionLevel mutation */
    case actions.APPLICATION_SELECTION_LEVEL_MUTATION_START: {
      if (action.payload.id.indexOf(state.get('application').id) !== -1) {
        const application = { ...state.get('application') };
        // Callers now send the integer selectionLevelValue (the enum is deprecated). Update it
        // optimistically so the single-application view reflects the new level immediately (this
        // view is not refetched after the mutation). Keep the enum in sync when provided.
        if (
          action.payload.selectionLevelValue !== undefined &&
          action.payload.selectionLevelValue !== null
        ) {
          application.selectionLevelValue = action.payload.selectionLevelValue;
        }
        if (action.payload.selectionLevel !== undefined) {
          application.selectionLevel = action.payload.selectionLevel;
        }
        return state.set('application', application);
      }
      return state;
    }

    /* interview attendance toggle */
    case actions.APPLICATION_INTERVIEW_ATTENDANCE_TOGGLE:
      return state.set('hasCandidateMissedAnyInterview', action.payload);

    /* application list reducer */
    case actions.APPLICATIONS_FETCHING_DONE:
      return state.set('applications', action.payload);
    case actions.APPLICATIONS_LOADING_DONE:
      return state.set('applications_loading', false);
    case actions.APPLICATIONS_FETCH:
      return state.set('applications_loading', true);

    /* comments sidebar */
    case actions.SHOW_COMMENTS:
      return state.set('showComments', true);
    case actions.HIDE_COMMENTS:
      return state.set('showComments', false);

    /* tags mutation */
    case actions.APPLICATION_TAG_MUTATION_REMOVE_START: {
      /** remove now, saga will populate with new tag list anyway
       * but this looks better for the user
       */
      const { tagId } = action.payload;
      return state.set('application', {
        ...state.get('application'),
        tags: state.get('application').tags.filter((tag) => tag.id !== tagId),
      });
    }

    case actions.APPLICATION_TAG_MUTATION_ADD_START: {
      /** add now, saga will populate with new tag list anyway
       * but this looks better for the user
       */
      const tag = action.payload;
      const { tagId } = tag;
      const { tags, ...application } = state.get('application');
      /* filter to check if already on the list */
      if (!tags.some((item) => item.id === tagId)) {
        const newTag = {
          id: tag.tagId,
          name: tag.name,
          special_type: '',
          __typename: 'Tag',
        };
        const updatedTags = [...tags, newTag];
        const updatedApplication = { ...application, tags: updatedTags };
        return state.set('application', updatedApplication);
      }
      return state;
    }

    case actions.APPLICATION_TAGS_FETCH_DONE:
      if (state.get('application').id === action.payload.id) {
        // PPP hack, we need to reload the pay per performance data if a tag refresh happens
        return state.set('application', action.payload).set('loadPayPerPerformance', true);
      }
      return state;

    case actions.APPLICATION_EVENTS_FETCH_DONE:
      if (state.get('application').id === action.payload.id) {
        return state.set('application', action.payload);
      }
      return state;

    case actions.APPLICATION_COMMENTS_FETCH_DONE:
      if (state.get('application').id === action.payload.id) {
        return state.set('application', action.payload);
      }
      return state;

    case actions.APPLICATION_RATING_FETCH_DONE:
      if (state.get('application').id === action.payload.id) {
        return state.set('application', action.payload);
      }
      return state;

    case actions.APPLICATION_SELECTION_LEVEL_FETCH_DONE:
      if (state.get('application').id === action.payload.id) {
        return state.set('application', action.payload);
      }
      return state;

    case actions.APPLICATION_HIDE_START:
      return state.set('application', {
        ...state.get('application'),
        hidden: action.payload.state,
      });

    case projectActions.APPLICATION_RELEVANCE_REFRESH_DONE: {
      if (state.get('application').id === action.payload.id) {
        return state.set('application', {
          ...state.get('application'),
          relevance: action.payload.relevance,
          processingStatus: action.payload.processingStatus,
          summary: action.payload.summary,
          feedback: action.payload.feedback,
        });
      }
      return state;
    }

    case actions.APPLICATION_GDPRED:
      return state.set('applicationGdpred', true);

    case actions.APPLICATION_PAY_PER_PERFORMANCE_FETCH_START:
      return state
        .set('applicationPayPerPerformanceLoading', true)
        .set('loadPayPerPerformance', false);
    case actions.APPLICATION_PAY_PER_PERFORMANCE_FETCH_DONE:
      return state
        .set('applicationPayPerPerformance', action.payload)
        .set('applicationPayPerPerformanceLoading', false)
        .set('loadPayPerPerformance', false);
    case actions.APPLICATION_PAY_PER_PERFORMANCE_FETCH_ERROR:
    case actions.APPLICATION_PAY_PER_PERFORMANCE_FETCH_CANCELLED:
      return state
        .set('applicationPayPerPerformanceLoading', false)
        .set('loadPayPerPerformance', false);

    case actions.APPLICATION_PAY_PER_PERFORMANCE_RESET:
      return state
        .set('applicationPayPerPerformance', undefined)
        .set('applicationPayPerPerformanceLoading', false)
        .set('loadPayPerPerformance', false);

    case actions.APPLICATION_UNLOCK_PAY_PER_PERFORMANCE_START:
      return state.set('applicationUnlockPayPerPerformanceMutating', true);
    case actions.APPLICATION_UNLOCK_PAY_PER_PERFORMANCE_DONE:
      return state.set('applicationUnlockPayPerPerformanceMutating', false);
    case actions.APPLICATION_UNLOCK_PAY_PER_PERFORMANCE_ERROR:
    case actions.APPLICATION_UNLOCK_PAY_PER_PERFORMANCE_CANCELLED:
      return state.set('applicationUnlockPayPerPerformanceMutating', false);
    case actions.APPLICATION_TAG_MUTATION_REMOVE_DONE:
    case actions.APPLICATION_TAG_MUTATION_ADD_DONE:
      return state.set('application', { ...state.get('application'), tags: action.payload.tags });
    case '@@router/LOCATION_CHANGE':
      return state.set('applicationCommentEditorVisible', false);

    case actions.APPLICATION_SET_CARD_WIDTH:
      return state.set('cardWidth', action.payload);

    /* default logout handler */
    case 'LOGOUT':
      return initState;
    default:
      return state;
  }
}
